import Phaser from 'phaser';
import { CampManager } from '../systems/CampManager';
import { MetaManager } from '../systems/MetaManager';
import { ITEM_DEFINITIONS, SLOT_LABELS, CONSUMABLE_DEFINITIONS } from '../data/items';
import { ItemInstance } from '../types/Item.types';
import { SfxManager } from '../utils/SfxManager';
import { ScrollablePanel } from '../ui/ScrollablePanel';
import { ResourceBanner } from '../ui/ResourceBanner';
import { formatResourceDrop } from '../utils/ResourceLoot';
import { ensureTouchTarget } from '../ui/MobileTouchUI';
import { GAME_W, GAME_H, GAME_CX, GAME_CY } from '../utils/MobileHelper';
import { startSceneWithFade } from '../utils/SceneUtils';
import { FONT_BODY } from '../utils/Typography';
import { sortStorageItems, formatItemBonusesInline, getRecycleResult } from '../utils/StorageHelpers';
import { IslandCampFlag } from '../systems/IslandCampFlag';

// === SCÈNE DU COFFRE ===

const RARITY_COLORS: Record<string, string> = {
  common: '#bdc3c7',
  uncommon: '#2ecc71',
  rare: '#f1c40f',
};

const HEADER_H = 70;

export class StorageScene extends Phaser.Scene {
  private panel!: ScrollablePanel;
  private camp!: CampManager;
  private meta!: MetaManager;
  private sfx = new SfxManager();

  constructor() {
    super({ key: 'StorageScene' });
  }

  init(data: { camp?: CampManager; meta?: MetaManager }): void {
    this.camp = data?.camp ?? new CampManager();
    this.meta = data?.meta ?? new MetaManager();
  }

  create(): void {
    this.cameras.main.fadeIn(200, 0, 0, 0);
    this.drawUI();
  }

  private drawUI(): void {
    // Détruire d'abord le panel précédent : sinon ses listeners (wheel/drag)
    // sur scene.input restent enregistrés et s'accumulent à chaque redraw.
    this.panel?.destroy();
    this.children.removeAll(true);

    this.add.rectangle(GAME_CX, GAME_CY, GAME_W, GAME_H, 0x0a0a1e).setDepth(0);

    // Bandeau ressources
    new ResourceBanner(this, this.camp.getResources());

    // === HEADER FIXE ===
    this.add.rectangle(GAME_CX, HEADER_H / 2, GAME_W, HEADER_H, 0x0a0a1e).setDepth(10);

    this.add.text(GAME_CX, 18, '📦 COFFRE DU CAMP', {
      fontSize: '20px', color: '#f1c40f', fontFamily: FONT_BODY, fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(11);

    const storage = this.camp.getStorage();
    this.add.text(GAME_CX, 45, `${storage.length}/${this.camp.getStorageCapacity()} items stockés`, {
      fontSize: '12px', color: '#7f8c8d', fontFamily: FONT_BODY,
    }).setOrigin(0.5).setDepth(11);

    const backBtn = this.add.text(15, 10, '← Camp', {
      fontSize: '11px', color: '#e74c3c', fontFamily: FONT_BODY,
      backgroundColor: '#2c1a1a', padding: { x: 10, y: 6 },
    }).setDepth(11);
    backBtn.setInteractive({ useHandCursor: true });
    backBtn.on('pointerdown', () => startSceneWithFade(this, IslandCampFlag.campSceneKey(), { camp: this.camp, meta: this.meta }));
    backBtn.on('pointerover', () => backBtn.setColor('#ff6b6b'));
    backBtn.on('pointerout', () => backBtn.setColor('#e74c3c'));

    // === CONTENU SCROLLABLE ===
    this.panel = new ScrollablePanel(this, 0, HEADER_H, GAME_W, GAME_H - HEADER_H);

    let y = 15;

    // === ÉQUIPEMENT ===
    const rowW = Math.min(560, GAME_W - 10);
    const lx = Math.round(GAME_CX - rowW / 2 + 8);

    if (storage.length === 0) {
      this.panel.add(this.add.text(GAME_CX, y + 30, 'Aucun équipement stocké.\n\nLes items récupérés en expédition\npeuvent être stockés ici.', {
        fontSize: '12px', color: '#7f8c8d', fontFamily: FONT_BODY, align: 'center',
      }).setOrigin(0.5));
      y += 80;
    } else {
      this.panel.add(this.add.text(lx, y, `--- ⚔️ Équipement (${storage.length}) ---`, {
        fontSize: '11px', color: '#f39c12', fontFamily: FONT_BODY,
      }));
      y += 22;
      // Affichage trié : slot → rareté → nom (la donnée du coffre est inchangée)
      const sortedStorage = sortStorageItems(storage, ITEM_DEFINITIONS);
      for (const item of sortedStorage) {
        y = this.drawStorageItem(item, y);
        y += 8;
      }
    }

    // === CONSOMMABLES ===
    const consumables = this.camp.getConsumables();
    y += 15;
    this.panel.add(this.add.text(lx, y, `--- 🧪 Consommables (${consumables.length}) ---`, {
      fontSize: '11px', color: '#9b59b6', fontFamily: FONT_BODY,
    }));
    y += 22;
    if (consumables.length === 0) {
      this.panel.add(this.add.text(GAME_CX, y + 8, '(aucun consommable stocké)', {
        fontSize: '11px', color: '#7f8c8d', fontFamily: FONT_BODY, fontStyle: 'italic',
      }).setOrigin(0.5));
      y += 30;
    } else {
      for (const c of consumables) {
        y = this.drawConsumable(c.uid, c.definitionId, y);
        y += 6;
      }
    }

    y += 30;
    this.panel.setContentHeight(y);
  }

  /** Affiche un consommable du coffre avec bouton jeter (avec confirmation). */
  private drawConsumable(uid: string, definitionId: string, y: number): number {
    const def = CONSUMABLE_DEFINITIONS[definitionId];
    if (!def) return y;
    const rowW = Math.min(560, GAME_W - 10);
    const lx = Math.round(GAME_CX - rowW / 2 + 8);
    const rx = Math.round(GAME_CX + rowW / 2 - 4);
    const color = RARITY_COLORS[def.rarity] ?? '#bdc3c7';

    const bg = this.add.rectangle(GAME_CX, y + 14, rowW, 32, 0x2d1e4a)
      .setStrokeStyle(1, parseInt(color.replace('#', ''), 16));
    this.panel.add(bg);

    this.panel.add(this.add.text(lx, y + 4, `${def.emoji} ${def.name}`, {
      fontSize: '10px', color, fontFamily: FONT_BODY, fontStyle: 'bold',
    }));
    this.panel.add(this.add.text(lx, y + 18, def.description, {
      fontSize: '11px', color: '#7f8c8d', fontFamily: FONT_BODY,
      wordWrap: { width: rowW * 0.7 },
    }));

    // Bouton jeter avec confirmation
    const btn = this.add.text(rx, y + 8, '[Jeter]', {
      fontSize: '10px', color: '#e67e22', fontFamily: FONT_BODY,
      backgroundColor: '#2a1a1a', padding: { x: 4, y: 2 },
    }).setOrigin(1, 0);
    btn.setInteractive({ useHandCursor: true });
    btn.on('pointerdown', () => {
      if (btn.getData('confirm')) {
        this.sfx.select();
        this.camp.withdrawConsumable(uid);
        this.drawUI();
      } else {
        btn.setData('confirm', true);
        btn.setText('⚠️ Confirmer ?');
        btn.setColor('#e74c3c');
        this.time.delayedCall(3000, () => {
          if (btn.active) {
            btn.setData('confirm', false);
            btn.setText('[Jeter]');
            btn.setColor('#e67e22');
          }
        });
      }
    });
    this.panel.add(btn);

    return y + 32;
  }

  private drawStorageItem(item: ItemInstance, y: number): number {
    const def = ITEM_DEFINITIONS[item.definitionId];
    if (!def) return y;
    const rowW = Math.min(560, GAME_W - 10);
    const lx = Math.round(GAME_CX - rowW / 2 + 8);
    const rx = Math.round(GAME_CX + rowW / 2 - 4);
    const color = RARITY_COLORS[def.rarity] ?? '#ffffff';
    const slotLabel = SLOT_LABELS[def.slot] ?? def.slot;
    const recycleResult = getRecycleResult(def.id, def.rarity);
    const recycleLabel = formatResourceDrop(recycleResult);
    const statsLabel = formatItemBonusesInline(def.bonuses);

    const ROW_H = 42;
    const bg = this.add.rectangle(GAME_CX, y + ROW_H / 2, rowW, ROW_H, 0x1e2d4a)
      .setStrokeStyle(1, parseInt(color.replace('#', ''), 16));
    this.panel.add(bg);

    // Ligne 1 : slot + nom (cliquable → popup détails)
    const nameText = this.add.text(lx, y + 5, `${slotLabel}  ${def.name}`, {
      fontSize: '10px', color, fontFamily: FONT_BODY,
    });
    nameText.setInteractive({ useHandCursor: true });
    nameText.on('pointerdown', () => this.showItemPopup(def));
    this.panel.add(nameText);

    // Ligne 2 : stats principales inline (sous le nom)
    if (statsLabel) {
      this.panel.add(this.add.text(lx, y + 23, statsLabel, {
        fontSize: '10px', color: '#ecf0f1', fontFamily: FONT_BODY,
      }));
    }

    // Bouton recycler avec confirmation
    const btn = this.add.text(rx, y + 3, `[♻ ${recycleLabel}]`, {
      fontSize: '10px', color: '#e67e22', fontFamily: FONT_BODY,
      backgroundColor: '#2a2a1a', padding: { x: 3, y: 2 },
    }).setOrigin(1, 0);
    btn.setInteractive({ useHandCursor: true });
    btn.on('pointerdown', () => {
      if (btn.getData('confirm')) {
        // Deuxième clic : recycler vraiment (l'UID dans la closure cible bien CET item)
        this.sfx.select();
        this.camp.withdrawItem(item.uid);
        this.camp.getResources().addMultiple(recycleResult);
        this.drawUI();
      } else {
        // Premier clic : demander confirmation (message renforcé pour les rares)
        btn.setData('confirm', true);
        const isRare = def.rarity === 'rare';
        btn.setText(isRare ? '⚠️ RARE ! Confirmer ?' : '⚠️ Confirmer ?');
        btn.setColor('#e74c3c');
        // Reset après 3s si pas confirmé
        this.time.delayedCall(3000, () => {
          if (btn.active) {
            btn.setData('confirm', false);
            btn.setText(`[♻ ${recycleLabel}]`);
            btn.setColor('#e67e22');
          }
        });
      }
    });
    btn.on('pointerover', () => btn.setColor(btn.getData('confirm') ? '#ff6b6b' : '#f39c12'));
    btn.on('pointerout', () => btn.setColor(btn.getData('confirm') ? '#e74c3c' : '#e67e22'));
    ensureTouchTarget(btn);
    this.panel.add(btn);

    return y + ROW_H;
  }

  shutdown(): void {
    this.panel?.destroy();
  }

  /** Popup avec les stats détaillées d'un item */
  private showItemPopup(def: import('../types/Item.types').ItemDefinition): void {
    const overlay = this.add.rectangle(GAME_CX, GAME_CY, GAME_W, GAME_H, 0x000000, 0.5).setDepth(150).setInteractive();
    const popup = this.add.rectangle(GAME_CX, 300, Math.min(380, GAME_W - 30), 200, 0x0d1a2e, 0.95).setStrokeStyle(2, 0xf1c40f).setDepth(151);

    const rarityColors: Record<string, string> = { common: '#bdc3c7', uncommon: '#2ecc71', rare: '#f1c40f' };
    const color = rarityColors[def.rarity] ?? '#ffffff';

    const title = this.add.text(GAME_CX, 230, `${def.emoji} ${def.name}`, {
      fontSize: '16px', color, fontFamily: FONT_BODY, fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(152);

    const desc = this.add.text(GAME_CX, 255, def.description, {
      fontSize: '11px', color: '#7f8c8d', fontFamily: FONT_BODY,
      wordWrap: { width: Math.min(370, GAME_W - 40) }, align: 'center',
    }).setOrigin(0.5).setDepth(152);

    const bonusParts: string[] = [];
    const b = def.bonuses;
    if (b.attack) bonusParts.push(`ATK ${b.attack > 0 ? '+' : ''}${b.attack}`);
    if (b.defense) bonusParts.push(`DEF ${b.defense > 0 ? '+' : ''}${b.defense}`);
    if (b.maxHp) bonusParts.push(`HP ${b.maxHp > 0 ? '+' : ''}${b.maxHp}`);
    if (b.pa) bonusParts.push(`PA ${b.pa > 0 ? '+' : ''}${b.pa}`);
    if (b.pm) bonusParts.push(`PM ${b.pm > 0 ? '+' : ''}${b.pm}`);
    if (b.range) bonusParts.push(`Portée +${b.range}`);
    if (b.capacity) bonusParts.push(`Sac +${b.capacity}`);

    const stats = this.add.text(GAME_CX, 285, bonusParts.join('   ') || '(aucun bonus)', {
      fontSize: '12px', color: '#ecf0f1', fontFamily: FONT_BODY,
    }).setOrigin(0.5).setDepth(152);

    const slotLabel = SLOT_LABELS[def.slot] ?? def.slot;
    const info = this.add.text(GAME_CX, 315, `${slotLabel}  •  ${def.rarity}`, {
      fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
    }).setOrigin(0.5).setDepth(152);

    const closeBtn = this.add.text(GAME_CX, 360, '[ Fermer ]', {
      fontSize: '12px', color: '#e74c3c', fontFamily: FONT_BODY,
      backgroundColor: '#2c1a1a', padding: { x: 10, y: 5 },
    }).setOrigin(0.5).setDepth(152);
    closeBtn.setInteractive({ useHandCursor: true });

    const elements = [overlay, popup, title, desc, stats, info, closeBtn];
    const close = () => elements.forEach(e => e.destroy());
    closeBtn.on('pointerdown', close);
    overlay.on('pointerdown', close);
  }
}
